import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["rankInput", "nameInput", "remainingPoints", "errorMessage"]
  static values = {
    maxPoints: Number
  }

  connect() {
    this.initializeInputs()
    this.listenToIntelligenceChanges()
    this.updateMaxPoints()
  }

  initializeInputs() {
    this.rankInputTargets.forEach(input => {
      input.addEventListener('input', (event) => this.handleSkillChange(event))
    })
    this.updatePoints()
  }

  listenToIntelligenceChanges() {
    const attributeController = document.querySelector('[data-controller="attribute-selection"]')
    if (!attributeController) return

    const intelligenceInput = attributeController.querySelector('input[name="character[intelligence]"]')
    if (!intelligenceInput) return

    intelligenceInput.addEventListener('input', () => this.updateMaxPoints())
    intelligenceInput.addEventListener('change', () => this.updateMaxPoints())
    this.updateMaxPoints()
  }

  updateMaxPoints() {
    const attributeController = document.querySelector('[data-controller="attribute-selection"]')
    if (attributeController) {
      const intelligenceInput = attributeController.querySelector('input[name="character[intelligence]"]')
      if (intelligenceInput) {
        const intelligence = this.creationEffectiveAttribute(attributeController, intelligenceInput, 'intelligence')
        this.maxPointsValue = intelligence * 2
        this.updatePoints()
        return
      }
    }

    const intelligenceValue = this.element.getAttribute('data-knowledge-selection-max-points-value')
    if (intelligenceValue) {
      this.maxPointsValue = parseInt(intelligenceValue, 10) || 0
      this.updatePoints()
    }
  }

  updatePoints() {
    const totalUsedPoints = this.rankInputTargets.reduce((sum, input) => {
      const value = parseInt(input.value, 10) || 0
      return sum + value
    }, 0)

    const remainingPoints = this.maxPointsValue - totalUsedPoints
    this.remainingPointsTarget.textContent = `Remaining: ${remainingPoints}`

    this.syncSubmitState()
  }

  handleSkillChange(event) {
    const input = event.target
    const newValue = parseInt(input.value, 10) || 0
    const rowIndex = input.dataset.rowIndex

    if (newValue > 0 && !this.hasKnowledgeNameForRow(rowIndex)) {
      input.value = 0
      this.showError("Enter a knowledge skill name before assigning points.")
      this.focusNameInput(rowIndex)
      this.updatePoints()
      return
    }

    let otherSkillsTotal = 0
    this.rankInputTargets.forEach(rankInput => {
      if (rankInput !== input) {
        otherSkillsTotal += parseInt(rankInput.value, 10) || 0
      }
    })

    const availablePoints = this.maxPointsValue - otherSkillsTotal
    const cappedValue = Math.min(newValue, availablePoints, 6)

    input.value = Math.max(cappedValue, 0)
    this.clearError()
    this.updatePoints()
  }

  incrementSkill(event) {
    const rowIndex = event.target.closest('button').dataset.rowIndex
    if (!this.hasKnowledgeNameForRow(rowIndex)) {
      this.showError("Enter a knowledge skill name before assigning points.")
      this.focusNameInput(rowIndex)
      return
    }

    const input = this.rankInputTargets.find(rankInput => rankInput.dataset.rowIndex === rowIndex)
    if (!input) return

    input.value = (parseInt(input.value, 10) || 0) + 1
    input.dispatchEvent(new Event('input', { bubbles: true }))
  }

  decrementSkill(event) {
    const input = this.rankInputTargets.find(rankInput => rankInput.dataset.rowIndex === event.target.closest('button').dataset.rowIndex)
    if (!input) return

    input.value = Math.max((parseInt(input.value, 10) || 0) - 1, 0)
    input.dispatchEvent(new Event('input', { bubbles: true }))
  }

  handleNameChange(event) {
    if (event.target.value.trim() !== '') {
      this.clearError()
    }
  }

  maxPointsValueChanged() {
    this.updatePoints()
  }

  creationEffectiveAttribute(attributeController, input, attributeName) {
    const baseValue = parseInt(input.value, 10) || 0
    const heritageMods = this.getAttributeModifications(attributeController, 'heritage')
    const magicMods = this.getAttributeModifications(attributeController, 'magic')

    return baseValue + (heritageMods[attributeName] || 0) + (magicMods[attributeName] || 0)
  }

  getAttributeModifications(attributeController, source) {
    try {
      const modsValue = attributeController.getAttribute(`data-attribute-selection-${source}-modifications-value`)
      return modsValue ? JSON.parse(modsValue) : {}
    } catch (e) {
      console.warn(`Failed to parse ${source} modifications:`, e)
      return {}
    }
  }

  syncSubmitState() {
    const submitButton = document.querySelector('button[type="submit"]')
    if (!submitButton) return

    const remainingTargets = document.querySelectorAll(
      '[data-attribute-selection-target="remainingPoints"], [data-skill-selection-target="remainingPoints"], [data-knowledge-selection-target="remainingPoints"], [data-etiquette-selection-target="remainingPoints"]'
    )
    const hasNegativeRemaining = Array.from(remainingTargets).some((target) => {
      const match = target.textContent.match(/-?\d+/)
      return match && parseInt(match[0], 10) < 0
    })

    submitButton.disabled = hasNegativeRemaining
    submitButton.classList.toggle('opacity-50', hasNegativeRemaining)
    submitButton.classList.toggle('cursor-not-allowed', hasNegativeRemaining)
  }

  hasKnowledgeNameForRow(rowIndex) {
    const input = this.nameInputTargets.find(nameInput => nameInput.dataset.rowIndex === rowIndex)
    return input && input.value.trim() !== ''
  }

  focusNameInput(rowIndex) {
    const input = this.nameInputTargets.find(nameInput => nameInput.dataset.rowIndex === rowIndex)
    if (input) input.focus()
  }

  showError(message) {
    if (!this.hasErrorMessageTarget) return

    this.errorMessageTarget.textContent = message
    this.errorMessageTarget.classList.remove('hidden')
  }

  clearError() {
    if (!this.hasErrorMessageTarget) return

    this.errorMessageTarget.textContent = ''
    this.errorMessageTarget.classList.add('hidden')
  }
}
